import { useEffect, useMemo, useRef, useState } from 'react'

import type { RestaurantPrivateSupplierDto } from '../api/restaurantMyPrices'
import { SelectCombobox } from './SelectCombobox'

export type ImportSupplierConfirmModalProps = {
  open: boolean
  targetLabel: string
  privateSuppliers: RestaurantPrivateSupplierDto[]
  /** Current target id when mode is existing; null for new supplier. */
  currentPrivateSupplierId: number | null
  onConfirm: (privateSupplierId: number | null) => void
  onDismiss: () => void
}

const supplierLabel = (supplier: RestaurantPrivateSupplierDto): string =>
  `${supplier.displayLabel || supplier.legalName} (ИНН ${supplier.inn})`

/**
 * Confirm which private-supplier catalog receives the import.
 * «Нет» → dropdown to pick another profile; selecting one asks again.
 */
export const ImportSupplierConfirmModal = ({
  open,
  targetLabel,
  privateSuppliers,
  currentPrivateSupplierId,
  onConfirm,
  onDismiss,
}: ImportSupplierConfirmModalProps) => {
  const dialogRef = useRef<HTMLDivElement>(null)
  const [showReselect, setShowReselect] = useState(false)
  const [pendingSupplierId, setPendingSupplierId] = useState<number | null>(currentPrivateSupplierId)
  const [pendingLabel, setPendingLabel] = useState(targetLabel)

  useEffect(() => {
    if (!open) {
      return
    }
    setShowReselect(false)
    setPendingSupplierId(currentPrivateSupplierId)
    setPendingLabel(targetLabel)
  }, [open, currentPrivateSupplierId, targetLabel])

  useEffect(() => {
    if (!open) {
      return
    }
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onDismiss()
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    dialogRef.current?.focus()
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [open, onDismiss])

  const reselectOptions = useMemo(
    () =>
      privateSuppliers.map((supplier) => ({
        value: String(supplier.id),
        label: supplierLabel(supplier),
      })),
    [privateSuppliers],
  )

  if (!open) {
    return null
  }

  const handleReselect = (next: string) => {
    if (!next) {
      return
    }
    const id = Number(next)
    const supplier = privateSuppliers.find((item) => item.id === id)
    if (!supplier) {
      return
    }
    setPendingSupplierId(id)
    setPendingLabel(supplier.legalName || supplier.displayLabel || supplierLabel(supplier))
    setShowReselect(false)
  }

  return (
    <div className="market-paywall-overlay" role="presentation" onClick={onDismiss}>
      <div
        ref={dialogRef}
        className="market-paywall-modal market-import-confirm-modal"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="import-supplier-confirm-title"
        aria-describedby="import-supplier-confirm-message"
        tabIndex={-1}
        onClick={(event) => event.stopPropagation()}
      >
        <h2 id="import-supplier-confirm-title" className="market-paywall-modal__title">
          Подтверждение импорта
        </h2>
        <p id="import-supplier-confirm-message" className="market-paywall-modal__message">
          Вы загружаете прайс в каталог «{pendingLabel}». Уверены?
        </p>

        {showReselect ? (
          <SelectCombobox
            className="market-import-confirm-modal__reselect"
            label="Выберите поставщика"
            value=""
            options={reselectOptions}
            placeholder="Начните вводить название"
            listAllOnFocus
            onChange={handleReselect}
          />
        ) : null}

        <div className="market-paywall-modal__actions">
          <button
            type="button"
            className="echat-btn echat-btn--ghost"
            onClick={() => {
              if (privateSuppliers.length === 0) {
                onDismiss()
                return
              }
              setShowReselect(true)
            }}
          >
            Нет
          </button>
          <button
            type="button"
            className="echat-btn echat-btn--primary"
            onClick={() => onConfirm(pendingSupplierId)}
          >
            Да, уверен
          </button>
        </div>
      </div>
    </div>
  )
}
